import { useCallback, useEffect, useRef, useState } from 'react';
import { getPlanosGaleria, type PlanoGaleriaItem } from '../../api/planos.js';

/** Cada cuánto se vuelve a preguntar mientras alguna vista previa se está preparando. */
const REVISAR_MS = 4000;
/** Reintentos antes de decir que no se pudo cargar. */
const REINTENTOS = 3;

/**
 * Planos del proyecto para la galería y la tira. Mientras alguna miniatura esté "preparándose",
 * se vuelve a preguntar solo (la hace el servidor en segundo plano). `recargar` sirve tras subir.
 */
export function usePlanosGaleria(projectId: string | null | undefined) {
  const [planos, setPlanos] = useState<PlanoGaleriaItem[] | null>(null);
  const [error, setError] = useState(false);
  const vivoRef = useRef(true);

  // Si falla (p. ej. el servidor se reinicia) NO se concluye que no hay planos: se reintenta sola
  // (1 s, 2 s, 4 s) y, si sigue fallando, `error` queda en true para ofrecer "Reintentar".
  const recargar = useCallback(async () => {
    if (!projectId) return;
    for (let intento = 0; ; intento++) {
      try {
        const ps = await getPlanosGaleria(projectId);
        if (!vivoRef.current) return;
        setPlanos(ps);
        setError(false);
        return;
      } catch {
        if (!vivoRef.current) return;
        if (intento >= REINTENTOS) {
          setError(true);
          return;
        }
        await new Promise((res) => setTimeout(res, 1000 * 2 ** intento));
      }
    }
  }, [projectId]);

  useEffect(() => {
    vivoRef.current = true;
    setPlanos(null);
    void recargar();
    return () => {
      vivoRef.current = false;
    };
  }, [recargar]);

  const hayPendientes = (planos ?? []).some((p) => p.miniatura === 'pending');
  useEffect(() => {
    if (!hayPendientes) return;
    const t = setInterval(() => void recargar(), REVISAR_MS);
    return () => clearInterval(t);
  }, [hayPendientes, recargar]);

  return { planos, error, recargar };
}

/**
 * El plano "principal" para abrir directo desde "Armar presupuesto de los planos": el que mide
 * (no "Solo referencia") y lleva más avance; a igualdad, el primero que se subió. null si no hay planos.
 */
export function planoPrincipal(planos: readonly PlanoGaleriaItem[]): PlanoGaleriaItem | null {
  if (planos.length === 0) return null;
  const miden = planos.filter((p) => p.rol !== 'referencia_spec');
  const candidatos = miden.length > 0 ? miden : planos;
  return candidatos.reduce((mejor, p) => (p.renglones > mejor.renglones ? p : mejor), candidatos[0]!);
}
